//libs
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

//selectors
import { isLoggedIn as _isLoggedIn } from 'store/app/selectors';

//constants
import { NUDGE_SOURCES } from './constants';
const LOGIN_LOCATION = 'bottomDrawer';

//components
import Container from './NudgeContainer';

//context
import NudgeContext from './context';
import { useDwebCartContext } from 'shared/components/DwebCart/context';
import { getCachedCustomerData } from 'shared/helpers/userCache';

//helpers
import { isSessionOver, setStorage } from './helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';

//types
import { ILoginNudge } from './types';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

const STORAGE_KEYS = {
  LISTING_LOADS: 'nf_nudge_listing_loads',
  FILTERS_APPLIED: 'nf_nudge_filters',
  PDP_VIEWS: 'nf_nudge_pdp_views',
  SESSION_START: 'nf_nudge_session_start',
  PLP_PAGE_START: 'nf_nudge_plp_page_start', // Time when user landed on PLP page
  PDP_PAGE_START: 'nf_nudge_pdp_page_start', // Time when user landed on PDP page
  NUDGE_SHOWN: 'nf_nudge_shown', // Track if nudge was already shown in this session
};

// In-memory cache for session data (updated immediately, persisted with debounce)
const sessionDataCache: Record<string, number> = {};

// Debounce timers for each storage key
const debounceTimers: Record<string, NodeJS.Timeout | null> = {};

const getSessionData = () => {
  try {
    return {
      listingLoads: sessionDataCache[STORAGE_KEYS.LISTING_LOADS] ?? parseInt(sessionStorage.getItem(STORAGE_KEYS.LISTING_LOADS) || '0', 10),
      filtersApplied: sessionDataCache[STORAGE_KEYS.FILTERS_APPLIED] ?? parseInt(sessionStorage.getItem(STORAGE_KEYS.FILTERS_APPLIED) || '0', 10),
      pdpViews: sessionDataCache[STORAGE_KEYS.PDP_VIEWS] ?? parseInt(sessionStorage.getItem(STORAGE_KEYS.PDP_VIEWS) || '0', 10),
      plpPageStart: sessionDataCache[STORAGE_KEYS.PLP_PAGE_START] ?? parseInt(sessionStorage.getItem(STORAGE_KEYS.PLP_PAGE_START) || '0', 10),
      pdpPageStart: sessionDataCache[STORAGE_KEYS.PDP_PAGE_START] ?? parseInt(sessionStorage.getItem(STORAGE_KEYS.PDP_PAGE_START) || '0', 10),
    };
  } catch {
    return { listingLoads: 0, filtersApplied: 0, pdpViews: 0, sessionStart: 0, plpPageStart: 0, pdpPageStart: 0 };
  }
};

const setSessionData = (key: string, value: number) => {
  // Update in-memory cache immediately
  sessionDataCache[key] = value;

  // Clear existing debounce timer for this key
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }

  // Debounce the actual sessionStorage write by 1 seconds
  debounceTimers[key] = setTimeout(() => {
    try {
      sessionStorage.setItem(key, value.toString());
    } catch {
      // Silent fail
    }
  }, 1000);
};

const getPageTimeElapsed = (pageType: string): number => {
  const data = getSessionData();
  const pageStart = pageType.startsWith('plp') ? data.plpPageStart : data.pdpPageStart;
  if (!pageStart) return 0;
  return Math.floor((Date.now() - pageStart) / 1000); // seconds
};

const initializePageTime = (pageType: string) => {
  const data = getSessionData();
  const key = pageType.startsWith('plp') ? STORAGE_KEYS.PLP_PAGE_START : STORAGE_KEYS.PDP_PAGE_START;
  // Only initialize if not already set
  if (!data[pageType.startsWith('plp') ? 'plpPageStart' : 'pdpPageStart']) {
    setSessionData(key, Date.now());
  }
};

const resetPageTime = (pageType: string) => {
  const key = pageType.startsWith('plp') ? STORAGE_KEYS.PLP_PAGE_START : STORAGE_KEYS.PDP_PAGE_START;
  try {
    sessionStorage.removeItem(key);
    delete sessionDataCache[key];
  } catch {
    // Silent fail
  }
};

const isNudgeShownInSession = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.NUDGE_SHOWN) === 'true';
  } catch {
    return false;
  }
};

const markNudgeAsShown = () => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.NUDGE_SHOWN, 'true');
  } catch {
    // Silent fail
  }
};

export default function LoginNudge({ children, page }: ILoginNudge) {
  const [nudgeVisible, setNudgeVisible] = useState(false);
  const isLoggedIn = useSelector(_isLoggedIn);
  const [isCrossTabLoggedIn, setIsCrossTabLoggedIn] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const nudgeSource = useRef(NUDGE_SOURCES.SCROLL_NUDGE);
  const [triggerChecked, setTriggerChecked] = useState(false);
  const [currentVariant, setCurrentVariant] = useState<string>('');
  const [sessionTime, setSessionTime] = useState<number>(24);
  const LOGIN_NUDGE_CONFIG = useRemoteConfig('loginNudge', { PLP: {}, PDP: {} });
  const { canShowCart } = useDwebCartContext();
  const isEffectivelyLoggedIn = isLoggedIn || isCrossTabLoggedIn;

  const syncCrossTabLoginState = useCallback(() => {
    const cachedCustomerData = getCachedCustomerData();
    const customerId = (cachedCustomerData as { id?: string } | undefined)?.id;
    setIsCrossTabLoggedIn(Boolean(customerId));
  }, []);
  /**
   * Check if PLP variant conditions are met
   */
  const checkPLPConditions = useCallback((variantName: string): boolean => {
    const config = LOGIN_NUDGE_CONFIG?.PLP?.[variantName];
    if (!config) return false;
    
    const data = getSessionData();
    const timeElapsed = getPageTimeElapsed('plp');
    
    return (
      data.listingLoads >= config.minListingLoads ||
      data.filtersApplied >= config.minFiltersApplied ||
      timeElapsed >= config.minTimeElapsed
      );
  }, [LOGIN_NUDGE_CONFIG?.PLP]);

  /**
   * Check if PDP variant conditions are met
   */
  const checkPDPConditions = useCallback((variantName: string): boolean => {
    const config = LOGIN_NUDGE_CONFIG?.PDP?.[variantName];
    if (!config) return false;
    
    const data = getSessionData();
    const timeElapsed = getPageTimeElapsed('pdp');
    return (data.pdpViews >= config.minListingLoads) || (timeElapsed >= config.minTimeElapsed);
  }, [LOGIN_NUDGE_CONFIG?.PDP]);

  /**
   * Check if conditions are met based on page type and variant
   */
  const checkConditions = useCallback(
    (pageType: string, variantName: string): boolean => {
      if (pageType.startsWith("plp")) {
        return checkPLPConditions(variantName);
      } else if (pageType.startsWith("pdp")) {
        return checkPDPConditions(variantName);
      }
      return false;
    },
    [checkPLPConditions, checkPDPConditions]
  );

  /**
   * Show nudge if all conditions are met
   * This will be called on page load and URL changes
   */
  const showNudge = useCallback(
    (sessionTime: number, source: string, variant: string) => {
      // Don't show nudge if auth hasn't initialized yet, user is logged in, or cart is open
      const isCartOpen =
        canShowCart ||
        (typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('showCart') === '1');
      if (!isAuthInitialized || isEffectivelyLoggedIn || isCartOpen) {
        return;
      }
      // Store variant and session time for auto-check timer
      if (!currentVariant) {
        setCurrentVariant(variant);
        setSessionTime(sessionTime);
      }

      // Don't show if already visible, user is logged in, or session not over
      if (nudgeVisible || isEffectivelyLoggedIn || (variant === 'login-experience-c' &&  !isSessionOver())) {
        return;
      }

      // Don't show if nudge was already shown in this session
      if (isNudgeShownInSession() && variant !== 'login-experience-c') {
        return;
      }

      // For varC (control), maintain original behavior - no additional conditions
      if (variant === 'login-experience-c') {
        if (!triggerChecked) {
          NFA.updateDataLayer({ loginLocation: LOGIN_LOCATION });
          setNudgeVisible(true);
          nudgeSource.current = source;
          setStorage(sessionTime);
          setTriggerChecked(true);
          markNudgeAsShown();
        }
        return;
      }

      // For varA and varB, check if variant conditions are met
      const conditionsMet = checkConditions(page, variant);
      if (conditionsMet && !triggerChecked) {
        NFA.updateDataLayer({ loginLocation: LOGIN_LOCATION });
        setNudgeVisible(true);
        nudgeSource.current = source;
        setStorage(sessionTime);
        setTriggerChecked(true);
        markNudgeAsShown();
      }
    },
    [nudgeVisible, isEffectivelyLoggedIn, isAuthInitialized, checkConditions, canShowCart, page, triggerChecked, currentVariant]
  );

  const hideNudge = useCallback(() => {
    setNudgeVisible(false);
  }, []);

  /**
   * Track listing load (for PLP)
   * Call this when pagination/infinite scroll loads more products
   */
  const trackListingLoad = useCallback(() => {
    const data = getSessionData();
    setSessionData(STORAGE_KEYS.LISTING_LOADS, data.listingLoads + 1);
  }, []);

  /**
   * Track filter application (for PLP)
   * Call this when user applies a filter
   */
  const trackFilterApplied = useCallback((count) => {
    setSessionData(STORAGE_KEYS.FILTERS_APPLIED, count);
  }, []);

  /**
   * Track PDP view (for PDP)
   * Call this when user views a product detail page
   */
  const trackPDPView = useCallback(() => {
    const data = getSessionData();
    setSessionData(STORAGE_KEYS.PDP_VIEWS, data.pdpViews + 1);
  }, []);

    
  // Track when auth state has been initialized
  useEffect(() => {
    // Give a small delay to let Redux store hydrate
    const timer = setTimeout(() => {
      setIsAuthInitialized(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [isLoggedIn]);

  // Sync login state from shared localStorage across tabs.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    syncCrossTabLoginState();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'NYKAA_CID' || event.key === null) {
        syncCrossTabLoginState();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncCrossTabLoginState();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', syncCrossTabLoginState);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', syncCrossTabLoginState);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [syncCrossTabLoginState]);

  // Hide nudge when user logs in
  useEffect(() => {
    if (isEffectivelyLoggedIn && nudgeVisible) {
      setNudgeVisible(false);
    }
  }, [isEffectivelyLoggedIn, nudgeVisible]);

  // Hide nudge when cart opens
  useEffect(() => {
    if (nudgeVisible && canShowCart) {
      setNudgeVisible(false);
    }
  }, [nudgeVisible, canShowCart]);

  // Initialize session on mount
  useEffect(() => {
    if (!isLoggedIn) {
      initializePageTime(page);
    }
  }, [isLoggedIn, page]);

  // Reset page time when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      resetPageTime(page);
    };
  }, [page]);

  // Auto-check conditions every 5 seconds
  useEffect(() => {
    // Don't run timer if auth not initialized, logged in, or nudge already shown
    if (!isAuthInitialized || isEffectivelyLoggedIn || triggerChecked || !currentVariant) {
      return;
    }

    const intervalId = setInterval(() => {
      // Check if nudge should be shown based on variant conditions
      if (!isNudgeShownInSession() && !triggerChecked) {
        showNudge(sessionTime, NUDGE_SOURCES.SCROLL_NUDGE, currentVariant);
      }
    }, 5000); // Run every 5 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthInitialized, isEffectivelyLoggedIn, triggerChecked, currentVariant, sessionTime, showNudge]);

  const values = useMemo(
    () => ({
      showNudge,
      hideNudge,
      nudgeSource: nudgeSource.current,
      trackListingLoad,
      trackFilterApplied,
      trackPDPView,
    }),
    [hideNudge, showNudge, trackListingLoad, trackFilterApplied, trackPDPView]
  );
  
  
  return (
    <NudgeContext.Provider value={values}>
      {children}
      {!isEffectivelyLoggedIn &&
        nudgeVisible &&
        !canShowCart &&
        <Container page={page} />}
    </NudgeContext.Provider>
  );
}
